import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { Building2, LogOut, MessageSquareText, Zap } from 'lucide-react';
import { Toaster } from 'react-hot-toast';

import { useAuthContext } from '../../context/AuthContext';
import { ArtifactsPanelProvider } from '../../context/ArtifactsPanelContext';
import { ChatProvider } from '../../context/ChatContext';
import { FileTreeProvider } from '../../context/FileTreeContext';
import { QuotaProvider } from '../../context/QuotaContext';
import QuotaPill from '../Quota/QuotaPill';
import ReportChatDrawer, { ReportChatContext } from './ReportChatDrawer';

export interface ReportsOutletContext {
  setReportChatContext: React.Dispatch<React.SetStateAction<ReportChatContext | null>>;
  openReportChat: () => void;
}

const ReportsLayoutContent: React.FC = () => {
  const { user, logout } = useAuthContext();
  const navigate = useNavigate();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [reportChatContext, setReportChatContext] = useState<ReportChatContext | null>(null);

  const openReportChat = useCallback(() => {
    if (reportChatContext) setIsChatOpen(true);
  }, [reportChatContext]);
  const closeReportChat = useCallback(() => setIsChatOpen(false), []);

  useEffect(() => {
    if (!reportChatContext) setIsChatOpen(false);
  }, [reportChatContext]);

  const outletContext = useMemo<ReportsOutletContext>(() => ({
    setReportChatContext,
    openReportChat,
  }), [openReportChat]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-app text-gray-900" dir="rtl">
      <header className="sticky top-0 z-30 border-b border-sidebar-700/70 bg-sidebar-950 text-white shadow-sm">
        <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-4 sm:px-6 lg:px-8">
          <Link to="/reports" className="flex min-h-11 items-center gap-2 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-600/20 text-primary-300">
              <Building2 className="h-5 w-5" aria-hidden="true" />
            </span>
            <span className="leading-tight">
              <span className="block text-sm font-semibold">דוחות ציבוריים</span>
              <span className="block text-[11px] text-sidebar-400">Financial AI</span>
            </span>
          </Link>

          <nav className="mr-2 hidden items-center gap-1 sm:flex" aria-label="ניווט דוחות">
            <NavLink
              to="/reports"
              end
              className={({ isActive }) => `flex min-h-11 items-center rounded-lg px-3 text-sm transition-colors ${
                isActive ? 'bg-sidebar-800 text-white' : 'text-sidebar-300 hover:bg-sidebar-800/70 hover:text-white'
              }`}
            >
              חברות במעקב
            </NavLink>
          </nav>

          <div className="mr-auto flex items-center gap-1.5">
            <button
              id="report-chat-trigger"
              type="button"
              onClick={openReportChat}
              disabled={!reportChatContext}
              aria-label={reportChatContext ? 'פתח צ׳אט על הדוח' : 'פתח דוח כדי לשאול עליו'}
              aria-expanded={isChatOpen}
              title={reportChatContext ? 'שאל על הדוח הנוכחי' : 'פתח דוח כדי לשאול עליו'}
              className="inline-flex min-h-11 items-center gap-2 rounded-lg px-3 text-sm text-sidebar-300 transition-colors hover:bg-sidebar-800 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-sidebar-300"
            >
              <MessageSquareText className="h-4 w-4" aria-hidden="true" />
              <span className="hidden sm:inline">שאל על הדוח</span>
            </button>
            <QuotaPill />
            <div className="hidden h-6 w-px bg-sidebar-700 sm:block" />
            <span className="hidden max-w-40 truncate px-2 text-xs text-sidebar-400 md:inline">
              {user?.email}
            </span>
            <button
              type="button"
              onClick={handleLogout}
              className="flex h-11 w-11 items-center justify-center rounded-lg text-sidebar-400 transition-colors hover:bg-sidebar-800 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
              aria-label="התנתק"
              title="התנתק"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <main className={`min-h-[calc(100vh-4rem)] transition-[padding] duration-200 ${isChatOpen ? 'lg:pl-[430px]' : ''}`}>
        <Outlet context={outletContext} />
      </main>

      <ReportChatDrawer isOpen={isChatOpen} context={reportChatContext} onClose={closeReportChat} />

      <div className={`pointer-events-none fixed bottom-4 left-4 hidden items-center gap-1.5 text-[10px] text-gray-400 lg:flex ${isChatOpen ? 'lg:hidden' : ''}`}>
        <Zap className="h-3 w-3" /> חילוץ דרך W3
      </div>

      <Toaster
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: { background: '#1f2937', color: '#fff' },
          success: { iconTheme: { primary: '#22c55e', secondary: '#fff' } },
          error: { duration: 5000, iconTheme: { primary: '#ef4444', secondary: '#fff' } },
        }}
      />
    </div>
  );
};

const ReportsLayout: React.FC = () => (
  <FileTreeProvider>
    <ChatProvider>
      <ArtifactsPanelProvider>
        <QuotaProvider>
          <ReportsLayoutContent />
        </QuotaProvider>
      </ArtifactsPanelProvider>
    </ChatProvider>
  </FileTreeProvider>
);

export default ReportsLayout;
